import {
  profileService,
  type UpdateProfileRequest,
} from "../../services/profile.service";
import {
  Camera,
  Edit,
  Loader2,
  Mail,
  Phone,
  User,
  User2Icon,
  X,
  Shield,
  Activity,
  LogOut,
  AlertTriangle,
  Clock,
  Lock,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  MapPin,
  Monitor,
  Smartphone,
} from "lucide-react";
import { useEffect, useState } from "react";
import Breadcrumbs from "../../widgets/Breadcurmbs";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";
import { useUser } from "../../contexts/UserInfoContext";
import Loader from "../../widgets/Loader";
import Tooltip from "../../widgets/ToolTip";
import { userActivityReportService } from "../../services/userActivityReport.service";
import { sessionManagementService } from "../../services/sessionManagement.service";
import authService from "../../services/auth.service";

export default function Profile() {
  const [profileResponse, setProfileResponse] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { userInfo, fetchUserInfo } = useUser();

  const [activities, setActivities] = useState<any[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesTotalCount, setActivitiesTotalCount] = useState(0);
  const activitiesPageSize = 10;
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [recentLogins, setRecentLogins] = useState(0);
  const [isRevokingOthers, setIsRevokingOthers] = useState(false);
  const [isRevokingAll, setIsRevokingAll] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => { },
  });

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<UpdateProfileRequest>();

  const fetchProfile = async () => {
    try {
      setIsLoading(true);

      // Use UserInfoContext data if available, otherwise fall back to profile service
      const defaultValues = {
        given_name: userInfo?.name.givenName || "",
        family_name: userInfo?.name.familyName || "",
        email:
          userInfo?.emails.find((email) => email.primary)?.value ||
          userInfo?.userName ||
          "",
        mobile_no:
          userInfo?.phoneNumbers.find((phone) => phone.type === "WORK")
            ?.value || "",
        user_id: userInfo?.user_id,
      };

      reset(defaultValues);
    } catch (err: any) {
      setError(err.message || "Failed to load profile");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserActivities = async (page: number = 1) => {
    if (!userInfo?.user_id) return;
    try {
      setIsLoadingActivities(true);
      const response = await userActivityReportService.getUserActivityReports({
        skip: (page - 1) * activitiesPageSize,
        limit: activitiesPageSize,
        user_id: userInfo.user_id,
      });
      setActivities(response.user_activity_reports || []);
      setActivitiesTotalCount(response.total_count || response.user_activity_reports?.length || 0);
      setActivitiesPage(page);

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];

      // Fetch recent logins count (last 30 days)
      const loginsResponse = await userActivityReportService.getUserActivityReports({
        skip: 0,
        limit: 1,
        user_id: userInfo.user_id,
        event_type: "LOGIN",
        start_date: thirtyDaysAgoStr,
      });
      setRecentLogins(loginsResponse.total_count || 0);

      // Fetch failed attempts count (last 30 days)
      const failedResponse = await userActivityReportService.getUserActivityReports({
        skip: 0,
        limit: 1,
        user_id: userInfo.user_id,
        event_type: "LOGIN_FAILED",
        start_date: thirtyDaysAgoStr,
      });
      setFailedAttempts(failedResponse.total_count || 0);
    } catch (err) {
      console.error("Error fetching user activities:", err);
    } finally {
      setIsLoadingActivities(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    if (userInfo?.user_id) {
      fetchUserActivities();
    }
  }, [userInfo]);

  // Update profile
  const onSubmit = async (data: UpdateProfileRequest) => {
    try {
      const updated = await profileService.updateProfile(data.user_id!, data);
      setProfileResponse(updated);
      toast.success("Profile updated successfully");
      fetchUserInfo();
      setIsEditMode(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to update profile.");
    }
  };

  // Upload profile picture
  const handleProfileImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file (PNG, JPG, etc.)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    try {
      setIsUploading(true);

      // Upload the image and get the URL
      const imageUrl = await profileService.uploadProfileImage(file);

      // Update the profile with the new image URL
      if (userInfo?.user_id) {
        const updated = await profileService.updateProfile(userInfo.user_id, {
          profile_image: imageUrl,
        });

        setProfileResponse({ user: updated });

        // Refresh user info to get the updated profile
        await fetchUserInfo();

        toast.success("Profile picture updated successfully");
      } else {
        toast.error("Unable to update profile - user ID not found");
      }
    } catch (err: any) {
      console.error("Profile image upload error:", err);
      toast.error(err.message || "Failed to upload profile picture.");
    } finally {
      setIsUploading(false);
      // Clear the input to allow re-selecting the same file if needed
      e.target.value = "";
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    if (profileResponse?.user || userInfo) {
      const defaultValues = {
        given_name:
          userInfo?.name.givenName || profileResponse?.user.given_name || "",
        family_name:
          userInfo?.name.familyName || profileResponse?.user.family_name || "",
        email:
          userInfo?.emails.find((email) => email.primary)?.value ||
          userInfo?.userName ||
          profileResponse?.user.email ||
          "",
        mobile_no:
          userInfo?.phoneNumbers.find((phone) => phone.type === "WORK")
            ?.value ||
          profileResponse?.user.mobile_number ||
          profileResponse?.user.mobile_no ||
          "",
      };
      reset(defaultValues);
    }
  };

  const handleRevokeOthers = () => {
    setConfirmModal({
      isOpen: true,
      title: "Sign Out Other Devices",
      message: "Are you sure you want to sign out other devices? This will invalidate all other active sessions except this current browser.",
      onConfirm: async () => {
        try {
          setIsRevokingOthers(true);
          await sessionManagementService.revokeOtherSessions();
          toast.success("Signed out of all other devices successfully");
          fetchUserActivities();
        } catch (error: any) {
          toast.error(error.message || "Failed to sign out other devices");
        } finally {
          setIsRevokingOthers(false);
        }
      }
    });
  };

  const handleRevokeAll = () => {
    setConfirmModal({
      isOpen: true,
      title: "Emergency Logout",
      message: "Are you sure you want to sign out from all devices? This will terminate all active sessions including your current login.",
      onConfirm: async () => {
        try {
          setIsRevokingAll(true);
          await sessionManagementService.revokeAllSessions();
          
          // Clear session immediately on frontend since token is already revoked
          authService.clearSession();
          localStorage.removeItem('auth_token');
          
          toast.success("Signed out from all devices. Redirecting to login...");
          setTimeout(() => {
            window.location.href = "/login";
          }, 1500);
        } catch (error: any) {
          toast.error(error.message || "Failed to sign out from all devices");
          setIsRevokingAll(false);
        }
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  if (!profileResponse && !userInfo) return null;

  // Use UserInfoContext data if available, fallback to profileResponse
  const profile = profileResponse?.user;
  const contextUserName = userInfo
    ? `${userInfo.name.givenName} ${userInfo.name.familyName}`
    : "";
  const userName =
    contextUserName ||
    profile?.full_name ||
    `${profile?.given_name || ""} ${profile?.family_name || ""}`.trim();
  const profileImage = userInfo?.profile_image || profile?.profile_image;
  const userInitials = userInfo
    ? `${userInfo.name.givenName
      .charAt(0)
      .toUpperCase()}${userInfo.name.familyName.charAt(0).toUpperCase()}`
    : userName
      .split(" ")
      .map((n) => n.charAt(0))
      .join("")
      .toUpperCase();
  const primaryEmail =
    userInfo?.emails.find((email) => email.primary)?.value ||
    userInfo?.userName ||
    profile?.email;

  // Helper colors
  const getEventTypeColor = (eventType: string) => {
    const colors = {
      USER_CREATED: "bg-green-100 text-green-800 border border-green-200",
      USER_UPDATED: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      USER_DELETED: "bg-red-100 text-red-800 border border-red-200",
      GROUP_CREATED: "bg-purple-100 text-purple-800 border border-purple-200",
      GROUP_UPDATED: "bg-indigo-100 text-indigo-800 border border-indigo-200",
      GROUP_DELETED: "bg-orange-100 text-orange-800 border border-orange-200",
      ROLE_CREATED: "bg-blue-100 text-blue-800 border border-blue-200",
      ROLE_UPDATED: "bg-cyan-100 text-cyan-800 border border-cyan-200",
      ROLE_DELETED: "bg-pink-100 text-pink-800 border border-pink-200",
      TOKEN_CREATED: "bg-teal-100 text-teal-800 border border-teal-200",
      TOKEN_DELETED: "bg-gray-100 text-gray-800 border border-gray-200",
      USER_LOGIN: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      LOGIN: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      LOGIN_FAILED: "bg-rose-100 text-rose-800 border border-rose-200",
      LOGOUT: "bg-slate-100 text-slate-800 border border-slate-200",
      REVOKE_OTHER_SESSIONS: "bg-amber-100 text-amber-800 border border-amber-200",
      REVOKE_ALL_SESSIONS: "bg-rose-100 text-rose-800 border border-rose-200",
      TEMPLATE_CREATED: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      TEMPLATE_UPDATED: "bg-amber-100 text-amber-800 border border-amber-200",
      TEMPLATE_DELETED: "bg-rose-100 text-rose-800 border border-rose-200",
    };
    return (
      colors[eventType as keyof typeof colors] || "bg-gray-100 text-gray-800 border border-gray-200"
    );
  };

  const cleanDescription = (desc: string) => {
    if (!desc) return "";
    const parts = desc.split(" at ");
    if (parts.length > 1) {
      const lastPart = parts[parts.length - 1];
      if (/\d/.test(lastPart)) {
        return parts.slice(0, -1).join(" at ");
      }
    }
    return desc;
  };

  const loginActivities = activities.filter(
    (act) => act.event_type === "USER_LOGIN" || act.event_type?.includes("LOGIN") || act.event_type === "TOKEN_CREATED"
  );
  const lastLoginTime = loginActivities.length > 0 ? loginActivities[0].event_time : userInfo?.last_login;

  const activitiesTotalPages = Math.ceil(activitiesTotalCount / activitiesPageSize);
  const activitiesStartItem = activitiesTotalCount === 0 ? 0 : (activitiesPage - 1) * activitiesPageSize + 1;
  const activitiesEndItem = Math.min(activitiesPage * activitiesPageSize, activitiesTotalCount);

  const getEventIconColorClasses = (eventType: string) => {
    if (eventType === "LOGIN_FAILED") {
      return { bg: "bg-rose-50 border-rose-100", text: "text-rose-600", icon: AlertTriangle };
    }
    if (eventType.includes("LOGIN") || eventType === "TOKEN_CREATED") {
      return { bg: "bg-teal-50 border-teal-100", text: "text-teal-600", icon: Shield };
    }
    if (eventType.includes("REVOKE")) {
      return { bg: "bg-rose-50 border-rose-100", text: "text-rose-600", icon: LogOut };
    }
    if (eventType.includes("USER")) {
      return { bg: "bg-emerald-50 border-emerald-100", text: "text-emerald-600", icon: User };
    }
    if (eventType.includes("GROUP")) {
      return { bg: "bg-purple-50 border-purple-100", text: "text-purple-600", icon: Activity };
    }
    if (eventType.includes("ROLE")) {
      return { bg: "bg-indigo-50 border-indigo-100", text: "text-indigo-600", icon: Shield };
    }
    return { bg: "bg-slate-50 border-slate-200", text: "text-slate-500", icon: Shield };
  };

  const breadcrumbItems = [
    {
      label: "Dashboard",
      icon: <LayoutDashboard className="w-4 h-4" />,
      path: "/dashboard",
    },
    {
      label: "My Profile",
      icon: <User className="w-4 h-4" />,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Custom Confirmation Modal */}
      <AnimatePresence>
        {confirmModal.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-6 overflow-hidden z-10"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="space-y-2 flex-1">
                  <h3 className="text-base font-bold text-slate-900">
                    {confirmModal.title}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {confirmModal.message}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
                  className="px-4 py-2 text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm bg-white"
                >
                  No, Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
                    confirmModal.onConfirm();
                  }}
                  className="px-4 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Yes, Confirm
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 px-6 py-4 shadow-sm mb-6 space-y-4">
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-600 shadow-sm">
              <User2Icon className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">My Profile</h1>
              <p className="text-xs text-slate-600">
                Manage your personal information and account settings
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
        {/* Left Column */}
        <div className="lg:col-span-1 space-y-6">
          {/* Card 1: Profile Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex flex-col items-center text-center">
            <div className="relative w-24 h-24 mb-4">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt={userName}
                  className={`w-full h-full rounded-full object-cover border-4 border-slate-100 shadow-sm ${isUploading ? "opacity-50" : ""
                    }`}
                />
              ) : (
                <div
                  className={`w-full h-full bg-indigo-600 rounded-full flex items-center justify-center border-4 border-slate-100 shadow-sm ${isUploading ? "opacity-50" : ""
                    }`}
                >
                  <span className="text-white text-2xl font-bold">
                    {userInitials}
                  </span>
                </div>
              )}
              <label
                htmlFor="profile-image-upload"
                className="absolute bottom-0 right-0 bg-slate-950 text-white p-2 rounded-full hover:bg-slate-800 cursor-pointer shadow transition-all hover:scale-110"
              >
                {isUploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
              </label>
              <input
                id="profile-image-upload"
                type="file"
                className="hidden"
                onChange={handleProfileImageUpload}
                disabled={isUploading}
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              />
            </div>

            <h2 className="text-lg font-bold text-slate-950 mb-1">{userName}</h2>
            <p className="text-xs text-slate-500 mb-3">{primaryEmail || "No email"}</p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
              <span className="w-1.5 h-1.5 mr-1.5 bg-green-500 rounded-full animate-pulse"></span>
              Active
            </span>
          </div>

          {/* Card 2: Account Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
            <h3 className="text-sm font-bold text-teal-900 flex items-center gap-2 border-b border-slate-100 pb-3">
              <Shield className="w-4 h-4 text-teal-600" />
              Account Information
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-xs text-slate-500 uppercase font-semibold">User ID</span>
                <p className="text-xs text-slate-800 font-mono select-all bg-slate-50 px-2 py-1.5 rounded border border-slate-100 mt-1 truncate">
                  {userInfo?.user_id || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase font-semibold">Last Login</span>
                <p className="text-xs text-slate-800 flex items-center gap-2 mt-1">
                  <Clock className="w-3.5 h-3.5 text-teal-500" />
                  {lastLoginTime ? new Date(lastLoginTime).toLocaleString() : "N/A"}
                </p>
              </div>
              {loginActivities.length > 0 && (loginActivities[0].meta_data?.ip_address || loginActivities[0].ip_address || loginActivities[0].meta_data?.ip) && (
                <div>
                  <span className="text-xs text-slate-500 uppercase font-semibold">Last Login IP</span>
                  <p className="text-xs text-slate-800 font-mono select-all bg-slate-50 px-2 py-1.5 rounded border border-slate-100 mt-1 truncate">
                    {loginActivities[0].meta_data?.ip_address || loginActivities[0].ip_address || loginActivities[0].meta_data?.ip}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Account Security & Session Management */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
            <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
              <div className="p-1.5 bg-amber-50 rounded-lg text-amber-600">
                <Lock className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">
                  Account Security
                </h3>
                <p className="text-[10px] text-slate-500">Monitor active status and secure your account</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Recent Logins Card */}
              <div className="bg-gradient-to-br from-emerald-50/60 to-teal-50/20 border border-emerald-100/70 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 relative overflow-hidden group">
                <div className="relative z-10 flex flex-col justify-between h-full">
                  <div>
                    <span className="text-[11px] text-slate-500 font-medium block">Recent Logins</span>
                    <span className="text-2xl font-semibold text-emerald-700 block mt-1">{recentLogins}</span>
                  </div>
                  <span className="text-[10px] text-emerald-600/90 font-medium block mt-2 bg-emerald-100/50 px-2 py-0.5 rounded-full w-max">
                    last 30 days
                  </span>
                </div>
              </div>

              {/* Failed Attempts Card */}
              <div className="bg-gradient-to-br from-rose-50/60 to-orange-50/20 border border-rose-100/70 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 relative overflow-hidden group">
                <div className="relative z-10 flex flex-col justify-between h-full">
                  <div>
                    <span className="text-[11px] text-slate-500 font-medium block">Failed Attempts</span>
                    <span className="text-2xl font-semibold text-rose-700 block mt-1">{failedAttempts}</span>
                  </div>
                  <span className={`text-[10px] font-medium block mt-2 px-2 py-0.5 rounded-full w-max ${
                    failedAttempts > 0 
                      ? "bg-rose-100/50 text-rose-600" 
                      : "bg-slate-100/60 text-slate-500"
                  }`}>
                    last 30 days
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <div>
                  <h4 className="text-xs font-bold text-slate-800">Session Management</h4>
                  <p className="text-[10px] text-slate-500">Manage active sessions across other browsers and devices</p>
                </div>
              </div>

              <div className="space-y-3 pt-1">
                <div>
                  <button
                    onClick={handleRevokeOthers}
                    disabled={isRevokingOthers || isRevokingAll}
                    type="button"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 bg-white rounded-xl hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-xs font-semibold shadow-sm hover:shadow"
                  >
                    {isRevokingOthers ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Signing out...</span>
                      </>
                    ) : (
                      <>
                        <LogOut className="w-3.5 h-3.5" />
                        <span>Sign Out Other Devices</span>
                      </>
                    )}
                  </button>
                  <span className="text-[9px] text-slate-400 block mt-1.5 text-center">
                    Keep this session, sign out all others.
                  </span>
                </div>

                <div>
                  <button
                    onClick={handleRevokeAll}
                    disabled={isRevokingOthers || isRevokingAll}
                    type="button"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-700 bg-red-50/30 rounded-xl hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-xs font-semibold shadow-sm hover:shadow"
                  >
                    {isRevokingAll ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Signing out...</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-3.5 h-3.5" />
                        <span>Emergency Logout</span>
                      </>
                    )}
                  </button>
                  <span className="text-[9px] text-red-400 block mt-1.5 text-center font-medium">
                    Sign out from all devices including this one.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Details (Form) */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
            <div className="flex justify-between items-center border-b border-slate-100 pb-4">
              <h2 className="text-base font-bold text-indigo-900 flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-600" />
                Profile Information
              </h2>
              <Tooltip content={isEditMode ? "Cancel" : "Edit Profile"}>
                <button
                  onClick={isEditMode ? handleCancel : () => setIsEditMode(true)}
                  type="button"
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-indigo-100 bg-white text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50/50 shadow-sm transition-all"
                >
                  {isEditMode ? (
                    <X className="w-4 h-4" />
                  ) : (
                    <Edit className="w-4 h-4" />
                  )}
                </button>
              </Tooltip>
            </div>

            <AnimatePresence mode="wait">
              {isEditMode ? (
                <motion.form
                  key="edit-form"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* First Name */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">
                        First Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          {...register("given_name", {
                            required: "First name is required",
                            pattern: {
                              value: /^[A-Za-z0-9\s]*$/,
                              message: "Only alphabets, numbers, and spaces are allowed"
                            }
                          })}
                          className="block w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Enter first name"
                        />
                      </div>
                      {errors.given_name && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.given_name.message}
                        </p>
                      )}
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">
                        Last Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          {...register("family_name", {
                            required: "Last name is required",
                            pattern: {
                              value: /^[A-Za-z0-9\s]*$/,
                              message: "Only alphabets, numbers, and spaces are allowed"
                            }
                          })}
                          className="block w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Enter last name"
                        />
                      </div>
                      {errors.family_name && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.family_name.message}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="email"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: "Please enter a valid email address",
                            },
                          })}
                          className="block w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Enter email"
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">
                        Phone Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="tel"
                          {...register("mobile_no", {
                            pattern: {
                              value: /^[0-9]{10,15}$/,
                              message: "Please enter a valid phone number",
                            },
                          })}
                          className="block w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Enter phone number"
                        />
                      </div>
                      {errors.mobile_no && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.mobile_no.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-800 transition-colors shadow-sm bg-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </button>
                  </div>
                </motion.form>
              ) : (
                <motion.div
                  key="view-mode"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-4">
                    <h3 className="text-xs font-bold text-slate-500 uppercase mb-1.5">
                      First Name
                    </h3>
                    <p className="text-sm font-semibold text-slate-800">
                      {userInfo?.name.givenName || profile?.given_name || "N/A"}
                    </p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-4">
                    <h3 className="text-xs font-bold text-slate-500 uppercase mb-1.5">
                      Last Name
                    </h3>
                    <p className="text-sm font-semibold text-slate-800">
                      {userInfo?.name.familyName || profile?.family_name || "N/A"}
                    </p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-4">
                    <h3 className="text-xs font-bold text-slate-500 uppercase mb-1.5 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-indigo-600" />
                      Email Address
                    </h3>
                    <p className="text-sm font-semibold text-slate-800">
                      {primaryEmail || "N/A"}
                    </p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-4">
                    <h3 className="text-xs font-bold text-slate-500 uppercase mb-1.5 flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-indigo-600" />
                      Phone Number
                    </h3>
                    <p className="text-sm font-semibold text-slate-800">
                      {userInfo?.phoneNumbers.find(
                        (phone) => phone.type === "WORK"
                      )?.value ||
                        profile?.mobile_no ||
                        profile?.mobile_number ||
                        "N/A"}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Card 5: Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">
            <h2 className="text-base font-bold text-purple-900 flex items-center gap-2 border-b border-slate-100 pb-4">
              <Activity className="w-5 h-5 text-purple-600" />
              Recent Activity
            </h2>

            {isLoadingActivities ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                <span className="text-xs text-slate-500 mt-2">Loading activities...</span>
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <p className="text-sm text-slate-500">No recent activity logged for your account.</p>
              </div>
            ) : (
              <>
                <div className="flow-root">
                  <ul className="-mb-8">
                    {activities.map((activity, activityIdx) => (
                      <li key={activity._id}>
                        <div className="relative pb-8">
                          {activityIdx !== activities.length - 1 ? (
                            <span className="absolute left-5 top-5 -ml-px h-full w-0.5 bg-slate-100" aria-hidden="true" />
                          ) : null}
                          <div className="relative flex items-start space-x-3">
                            <div className="relative">
                              {(() => {
                                const config = getEventIconColorClasses(activity.event_type);
                                const IconComponent = config.icon;
                                return (
                                  <div className={`h-10 w-10 rounded-full ${config.bg} border flex items-center justify-center`}>
                                    <IconComponent className={`h-4 w-4 ${config.text}`} />
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="min-w-0 flex-1 py-1">
                              <div className="flex flex-wrap justify-between items-start gap-1">
                                <div className="text-xs text-slate-800 flex flex-wrap items-center gap-2">
                                  <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${getEventTypeColor(activity.event_type)}`}>
                                    {activity.event_type.replace(/_/g, " ")}
                                  </span>
                                  <span className="font-normal text-slate-600">{cleanDescription(activity.event_description)}</span>
                                </div>
                                <div className="text-right text-[10px] text-slate-500 whitespace-nowrap">
                                  {new Date(activity.event_time).toLocaleString()}
                                </div>
                              </div>
                              {(() => {
                                const ua = activity.meta_data?.user_agent || activity.meta_data?.userAgent || "";
                                const lowerUa = ua.toLowerCase();
                                const isMobile = /mobile|android|iphone|ipad|phone/i.test(lowerUa);
                                
                                let browser = "Browser";
                                if (lowerUa.includes("firefox")) {
                                  browser = "Firefox";
                                } else if (lowerUa.includes("chrome") && !lowerUa.includes("chromium")) {
                                  browser = "Chrome";
                                } else if (lowerUa.includes("safari") && !lowerUa.includes("chrome")) {
                                  browser = "Safari";
                                } else if (lowerUa.includes("edge") || lowerUa.includes("edg/")) {
                                  browser = "Edge";
                                } else if (lowerUa.includes("opera") || lowerUa.includes("opr/")) {
                                  browser = "Opera";
                                } else if (lowerUa.includes("trident") || lowerUa.includes("msie")) {
                                  browser = "IE";
                                }

                                const deviceText = isMobile ? "Mobile Device" : `${browser} Browser`;
                                const ip = activity.meta_data?.ip_address || activity.ip_address || activity.meta_data?.ip;

                                return (
                                  <div className="text-[11px] text-slate-500 mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                                    {ip && (
                                      <span className="inline-flex items-center gap-1.5">
                                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                                        {ip}
                                      </span>
                                    )}
                                    <span className="inline-flex items-center gap-1.5">
                                      {isMobile ? (
                                        <Smartphone className="w-3.5 h-3.5 text-slate-400" />
                                      ) : (
                                        <Monitor className="w-3.5 h-3.5 text-slate-400" />
                                      )}
                                      {deviceText}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-slate-100 pt-4 mt-6">
                  <div className="text-xs text-slate-500">
                    Showing <span className="font-semibold">{activitiesStartItem}</span> to{" "}
                    <span className="font-semibold">{activitiesEndItem}</span> of{" "}
                    <span className="font-semibold">{activitiesTotalCount}</span> logs
                  </div>
                  {activitiesTotalPages > 1 && (
                    <div className="flex items-center space-x-1.5">
                      <button
                        onClick={() => fetchUserActivities(activitiesPage - 1)}
                        disabled={activitiesPage === 1}
                        className="relative inline-flex items-center p-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>

                      {Array.from({ length: Math.min(5, activitiesTotalPages) }, (_, i) => {
                        let pageNum;
                        if (activitiesTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (activitiesPage <= 3) {
                          pageNum = i + 1;
                        } else if (activitiesPage >= activitiesTotalPages - 2) {
                          pageNum = activitiesTotalPages - 4 + i;
                        } else {
                          pageNum = activitiesPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => fetchUserActivities(pageNum)}
                            className={`relative inline-flex items-center px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${activitiesPage === pageNum
                                ? "z-10 bg-purple-50 border-purple-500 text-purple-600"
                                : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => fetchUserActivities(activitiesPage + 1)}
                        disabled={activitiesPage === activitiesTotalPages}
                        className="relative inline-flex items-center p-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
